import { z } from 'zod';
import { SearchRequestSchema } from './query/ast';

export const VIEW_KINDS = ['list', 'table', 'board', 'calendar', 'timeline'] as const;
export const ViewKindSchema = z.enum(VIEW_KINDS);
export type ViewKind = z.infer<typeof ViewKindSchema>;

/**
 * Saved search. `query` is the search AST, validated against the field registry at save
 * time; `$me` is resolved per caller when the view runs (Bolt 5). `projectId` null =
 * cross-project.
 */
export const ViewSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid().nullable(),
  ownerId: z.uuid(),
  name: z.string().min(1).max(80),
  kind: ViewKindSchema,
  query: SearchRequestSchema,
  shared: z.boolean(),
  portalVisible: z.boolean(),
  position: z.int().min(0),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type View = z.infer<typeof ViewSchema>;

export const CreateViewSchema = z.strictObject({
  projectId: z.uuid().nullable().default(null),
  name: z.string().min(1).max(80),
  kind: ViewKindSchema,
  query: SearchRequestSchema,
  /** Requires `view.manage_shared` in the project. */
  shared: z.boolean().default(false),
  portalVisible: z.boolean().default(false),
  position: z.int().min(0).default(0),
});
export type CreateView = z.infer<typeof CreateViewSchema>;

export const UpdateViewSchema = z
  .strictObject({
    projectId: z.uuid().nullable(),
    name: z.string().min(1).max(80),
    kind: ViewKindSchema,
    query: SearchRequestSchema,
    shared: z.boolean(),
    portalVisible: z.boolean(),
    position: z.int().min(0),
  })
  .partial();
export type UpdateView = z.infer<typeof UpdateViewSchema>;

/** `GET /views?projectId=&mine=`; query-string booleans arrive as `true` / `false`. */
export const ViewListQuerySchema = z.object({
  projectId: z.uuid().optional(),
  mine: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});
export type ViewListQuery = z.infer<typeof ViewListQuerySchema>;
